import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Route, Switch, useLocation } from 'wouter';
import { Home } from './screens/Home';
import { LocalResults } from './screens/LocalResults';
import { Play } from './screens/Play';
import { About, Rules } from './screens/Rules';
import { SettingsScreen } from './screens/Settings';
import { Setup } from './screens/Setup';
import { ChallengeAccept, ChallengeNew } from './screens/Challenge';
import { MatchScreen } from './screens/Match';
import { Account } from './screens/Account';
import { LiveHub, LiveJoin } from './screens/Live';
import { Daily, Leaderboards, TournamentDetail, Tournaments } from './screens/Competitions';
import { Journal, Profile } from './screens/Profile';
import { FriendAccept, Friends, Team, TeamJoin } from './screens/Social';
import { Plus } from './screens/Plus';
import { Admin } from './screens/Admin';
import { Legal } from './screens/Legal';
import { onUpdateAvailable, applyUpdate } from './lib/pwa';

function UpdateBanner() {
  const { t } = useTranslation();
  const [ready, setReady] = useState(false);
  const [path] = useLocation();
  useEffect(() => onUpdateAvailable(() => setReady(true)), []);
  // Updates are applied only outside a running round (the game screen is not mounted).
  const inRound = path.startsWith('/play') || path.startsWith('/match');
  if (!ready || inRound) return null;
  return (
    <div className="banner info update" role="status">
      {t('pwa.updateReady')}{' '}
      <button className="btn small primary" onClick={() => applyUpdate()}>
        {t('pwa.reload')}
      </button>
    </div>
  );
}

function OfflineBanner() {
  const { t } = useTranslation();
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  if (online) return null;
  return (
    <div className="banner warn" role="status">
      {t('pwa.offline')}
    </div>
  );
}

export function App() {
  return (
    <>
      <OfflineBanner />
      <UpdateBanner />
      <Switch>
        <Route path="/" component={Home} />
        <Route path="/setup/:mode?">{(params) => <Setup params={params} />}</Route>
        <Route path="/play" component={Play} />
        <Route path="/results" component={LocalResults} />
        <Route path="/rules" component={Rules} />
        <Route path="/settings" component={SettingsScreen} />
        <Route path="/about" component={About} />
        <Route path="/challenge/new/:mode?">{(params) => <ChallengeNew params={params} />}</Route>
        <Route path="/c/:token">{(params) => <ChallengeAccept params={params} />}</Route>
        <Route path="/match/:id">{(params) => <MatchScreen params={params} />}</Route>
        <Route path="/account" component={Account} />
        <Route path="/live" component={LiveHub} />
        <Route path="/live/join/:token">{(params) => <LiveJoin params={params} />}</Route>
        <Route path="/leaderboards" component={Leaderboards} />
        <Route path="/tournaments" component={Tournaments} />
        <Route path="/tournaments/:id">{(params) => <TournamentDetail params={params} />}</Route>
        <Route path="/daily" component={Daily} />
        <Route path="/me" component={Profile} />
        <Route path="/journal" component={Journal} />
        <Route path="/friends" component={Friends} />
        <Route path="/f/:token">{(params) => <FriendAccept params={params} />}</Route>
        <Route path="/team" component={Team} />
        <Route path="/t/:token">{(params) => <TeamJoin params={params} />}</Route>
        <Route path="/plus" component={Plus} />
        <Route path="/admin" component={Admin} />
        <Route path="/legal/:doc">{(params) => <Legal params={params} />}</Route>
        <Route>
          <NotFound />
        </Route>
      </Switch>
    </>
  );
}

function NotFound() {
  const { t } = useTranslation();
  return (
    <main className="page">
      <h1>{t('errors.notFound')}</h1>
      <a href="/">{t('nav.home')}</a>
    </main>
  );
}
